import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { CSSTransition } from 'react-transition-group';
import { Card, CardActionArea, CardContent, Container, Typography } from '@mui/material';

import { getNameAccordingToInsuranceCategory } from '../../utils/customerUtils';
import { GET_CUSTOMERS, useReady } from '../../shared';
import { PageTitle } from '../../components/common';
import ErrorPlaceholder from '../errorPlaceholder';
import useShowOnboards from './useShowOnboards';
import useSwitchCustomer from './useSwitchCustomer';
import NavMenuSelectCustomer from '../NavMenu/NavMenuSelectCustomer';

import { StyledUserList } from './SelectUser.styled';

export default function SelectUser() {
  const intl = useIntl();

  const [uuid, setUuid] = useState(null);
  const [disableButtons, setDisableButtons] = useState(false);

  const { data: allCustomersData, loading: allCustomersLoading, error } = useQuery(GET_CUSTOMERS);

  const customers = Array.isArray(allCustomersData?.customers)
    ? Array.from(allCustomersData.customers).sort((customerA, customerB) => {
        // show active contracts first
        return customerA?.endedContract?.localeCompare(customerB?.endedContract);
      })
    : [];

  // Show onboarding slides if applicable
  useShowOnboards();

  // Switch customer if uuid is set
  useSwitchCustomer(uuid);

  const handleSelectCustomer = (customerUuid) => {
    setUuid(customerUuid);
    setDisableButtons(true);
  };

  const ready = useReady();

  if (!allCustomersLoading && !!customers) {
    return (
      <CSSTransition in={ready} timeout={600} classNames="slow-fade" unmountOnExit>
        <Container>
          <PageTitle>
            <Typography variant="h1">
              <FormattedMessage id="select.customer.insured.title" />
            </Typography>
          </PageTitle>

          <Typography variant="p">
            <FormattedMessage id="select.customer.insured.note" />
          </Typography>

          <StyledUserList>
            {customers?.map((customerItem) => {
              const {
                key,
                policyName,
                insuranceCategory,
                endedContract,
                uuid: customerUuid,
              } = customerItem;
              const contractEnded = endedContract === 'true';
              const policyDisplayName = contractEnded
                ? intl.formatMessage({ id: 'contract.placeholder.inactive' })
                : policyName;
              const customerName = getNameAccordingToInsuranceCategory(customerItem);

              return (
                <Card elevation={1} key={key} component="li">
                  <CardActionArea
                    onClick={() => handleSelectCustomer(customerUuid)}
                    disabled={contractEnded || disableButtons}
                    aria-label={`${customerName} - ${policyDisplayName}`}
                  >
                    <CardContent>
                      <NavMenuSelectCustomer
                        customerKey={key}
                        radioGroupName="select-customer-contract"
                        policyName={policyDisplayName}
                        customerName={customerName}
                        insuranceCategory={insuranceCategory}
                        isCurrentCustomer={false}
                        contractEnded={contractEnded}
                        disabled={contractEnded || disableButtons}
                        handleSelectCustomer={() => handleSelectCustomer(customerUuid)}
                      />
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </StyledUserList>
        </Container>
      </CSSTransition>
    );
  }

  if (error) {
    return <ErrorPlaceholder />;
  }

  return null;
}
